import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { buildTranslate } from 'locales'
import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import { FormControl, Modal } from 'react-bootstrap-five'
import { timeUtils } from 'utilities/timeUtils'
import { useUserTimeZone } from 'utilities/userTimeZone'
import '../PromoCodes.scss'
import DayTimePicker, {
  DAYS,
  DaySelection,
  validateTimeRangeDescription,
} from './DayTimePicker'
import MoneyInput from './MoneyInput'

const t = buildTranslate('promo_codes.create_edit.redemption_section')

type PromotionRedemptionSectionProps = {
  startsAt: string
  onStartsAtChange: (value: string) => void
  endsAt: string
  onEndsAtChange: (value: string) => void
  minimumSpend: string
  onMinimumSpendChange: (value: string) => void
  maximumDiscount: string
  onMaximumDiscountChange: (value: string) => void
  minimumSpendError?: string
  // Days-of-week restriction
  daysEnabled: boolean
  onDaysEnabledChange: (value: boolean) => void
  days: DaySelection
  onDaysChange: (days: DaySelection) => void
  // Time-of-day restriction
  timeEnabled: boolean
  onTimeEnabledChange: (value: boolean) => void
  startHour: number
  onStartHourChange: (hour: number) => void
  endHour: number
  onEndHourChange: (hour: number) => void
  timeDescription: string
  onTimeDescriptionChange: (value: string) => void
  // True when hydration detected a per-day hour configuration that the
  // wizard cannot losslessly represent (e.g. created via Campaign Builder).
  // We display a banner and disable the day/time toggles to prevent
  // accidentally flattening the merchant's existing setup.
  advancedRestrictionsLocked?: boolean
}

const RedemptionSection: React.FC<PromotionRedemptionSectionProps> = ({
  startsAt,
  onStartsAtChange,
  endsAt,
  onEndsAtChange,
  minimumSpend,
  onMinimumSpendChange,
  maximumDiscount,
  onMaximumDiscountChange,
  minimumSpendError,
  daysEnabled,
  onDaysEnabledChange,
  days,
  onDaysChange,
  timeEnabled,
  onTimeEnabledChange,
  startHour,
  onStartHourChange,
  endHour,
  onEndHourChange,
  timeDescription,
  onTimeDescriptionChange,
  advancedRestrictionsLocked = false,
}) => {
  const [showRestrictions, setShowRestrictions] = useState(false)
  // Whether each amount restriction is expanded. Kept DECOUPLED from the
  // field value (mirroring how `daysEnabled`/`timeEnabled` work) so that
  // clearing the input does not collapse the section or flip the toggle off.
  // Seeded from the value so a section that already has an amount opens; the
  // effects below re-open once edit-mode hydration lands a value after mount.
  const [maxDiscountOpen, setMaxDiscountOpen] = useState(
    Boolean(maximumDiscount)
  )
  const [minSpendOpen, setMinSpendOpen] = useState(Boolean(minimumSpend))
  useEffect(() => {
    if (maximumDiscount) setMaxDiscountOpen(true)
  }, [maximumDiscount])
  useEffect(() => {
    if (minimumSpend) setMinSpendOpen(true)
  }, [minimumSpend])
  // Stable id so the minimum-spend input (rendered inside the modal) can link
  // its aria-describedby to the error span (rendered in the summary view).
  const minimumSpendErrorId = 'minimum-spend-error'
  const userTimeZone = useUserTimeZone()
  // Timezone abbreviation for the given datetime, falling back to "now" in
  // the merchant's timezone when no datetime has been entered yet.
  const tzAbbrFor = (dt: string): string =>
    (dt ? moment.tz(dt, userTimeZone) : moment.tz(userTimeZone)).format('z')

  const hasRestrictions = Boolean(
    minimumSpend || maximumDiscount || daysEnabled || timeEnabled
  )

  // `validateTimeRangeDescription` returns a leaf i18n key (or null); we
  // resolve it here against the local buildTranslate prefix.
  const descriptionErrorLeaf = timeEnabled
    ? validateTimeRangeDescription(timeDescription)
    : null
  const descriptionError = descriptionErrorLeaf
    ? t(`modal.${descriptionErrorLeaf}`)
    : null

  // Start === end represents a single-hour window (e.g. 2:00pm-2:59pm),
  // which is valid. Only reject ranges where the end strictly precedes
  // the start.
  const timeError =
    timeEnabled && endHour < startHour ? t('modal.time_end_after_start') : null

  // Build a human-readable summary of which days are selected. When every
  // day is on the merchant hasn't actually narrowed anything, so we omit
  // the line entirely.
  const selectedDays = DAYS.filter(d => days[d])
  const daysSummary =
    selectedDays.length === 7
      ? null
      : selectedDays.map(d => t(`modal.day_short.${d}`)).join(', ')

  return (
    <div className="promo-section-card">
      <Text.Header4 className="mb-s">{t('title')}</Text.Header4>

      <div className="mb-m">
        <Text.BodyText5 bold className="d-block mb-xs">
          {t('valid_from')} ({tzAbbrFor(startsAt)})
        </Text.BodyText5>
        <FormControl
          type="datetime-local"
          value={startsAt}
          onChange={e => onStartsAtChange(e.target.value)}
          data-testid="starts-at-input"
          className="promo-input-narrow"
          aria-label={`${t('valid_from')} (${tzAbbrFor(startsAt)})`}
        />
      </div>

      <div className="mb-l">
        <Text.BodyText5 bold className="d-block mb-xs">
          {t('valid_until')} ({tzAbbrFor(endsAt)})
        </Text.BodyText5>
        <FormControl
          type="datetime-local"
          value={endsAt}
          onChange={e => onEndsAtChange(e.target.value)}
          data-testid="ends-at-input"
          className="promo-input-narrow"
          aria-label={`${t('valid_until')} (${tzAbbrFor(endsAt)})`}
        />
      </div>

      <div className="d-flex align-items-center justify-content-between mb-s">
        <Text.BodyText5 bold>{t('restrictions')}</Text.BodyText5>
        {hasRestrictions && (
          <button
            type="button"
            className="promo-text-btn"
            onClick={() => setShowRestrictions(true)}
            data-testid="edit-restrictions-btn"
          >
            {t('edit')}
          </button>
        )}
      </div>

      {hasRestrictions ? (
        <div data-testid="restrictions-summary">
          {maximumDiscount && (
            <Text.BodyText4 className="d-block mb-xs">
              {t('maximum_discount_summary', { amount: maximumDiscount })}
            </Text.BodyText4>
          )}
          {minimumSpend && (
            <Text.BodyText4 className="d-block mb-xs">
              {t('minimum_spend_summary', { amount: minimumSpend })}
            </Text.BodyText4>
          )}
          {minimumSpendError && (
            <span
              id={minimumSpendErrorId}
              role="alert"
              className="text-danger error-message font-size-14 d-block mt-xs"
              data-testid="minimum-spend-error"
            >
              {minimumSpendError}
            </span>
          )}
          {daysEnabled && daysSummary && (
            <Text.BodyText4 className="d-block mb-xs">
              {t('days_summary', { days: daysSummary })}
            </Text.BodyText4>
          )}
          {timeEnabled && (
            <Text.BodyText4 className="d-block mb-xs">
              {t('time_summary', {
                start: timeUtils.hourLabel(startHour, false),
                end: timeUtils.hourLabel(endHour, true),
              })}
            </Text.BodyText4>
          )}
        </div>
      ) : (
        <Button
          kind="secondary"
          size="small"
          onClick={() => setShowRestrictions(true)}
          data-testid="add-restriction-btn"
        >
          {t('add_restriction')}
        </Button>
      )}

      <Modal
        show={showRestrictions}
        onHide={() => setShowRestrictions(false)}
        centered
        data-testid="restrictions-modal"
      >
        <Modal.Header>
          <div>
            <Modal.Title>
              <Text.Header4>{t('modal.title')}</Text.Header4>
            </Modal.Title>
            <div className="mt-xs">
              <Text.BodyText4 className="d-block grey-70">
                {t('modal.description')}
              </Text.BodyText4>
            </div>
          </div>
          <button
            type="button"
            className="promo-modal-close"
            onClick={() => setShowRestrictions(false)}
            aria-label={t('modal.close')}
          >
            &times;
          </button>
        </Modal.Header>
        <Modal.Body>
          {advancedRestrictionsLocked && (
            <div
              id="advanced-locked-banner"
              className="promo-inline-error mb-m"
              role="alert"
              data-testid="advanced-locked-banner"
            >
              {t('modal.advanced_locked_banner')}
            </div>
          )}
          <div className="mb-l">
            <div className="d-flex align-items-start justify-content-between">
              <div className="mr-m">
                <Text.BodyText4 bold className="d-block mb-xxs">
                  {t('modal.maximum_discount_title')}
                </Text.BodyText4>
                <Text.BodyText5 className="grey-70">
                  {t('modal.maximum_discount_description')}
                </Text.BodyText5>
              </div>
              <input
                type="checkbox"
                role="switch"
                checked={maxDiscountOpen}
                onChange={e => {
                  setMaxDiscountOpen(e.target.checked)
                  // Clear the amount when disabling so the save payload drops
                  // the restriction. Do NOT seed a value when enabling — an
                  // empty field is immediately typeable, whereas a seeded "0"
                  // blocks entry and has to be deleted first.
                  if (!e.target.checked) onMaximumDiscountChange('')
                }}
                className="toggle-switch"
                data-testid="maximum-discount-toggle"
                aria-label={t('modal.maximum_discount_title')}
              />
            </div>
            {maxDiscountOpen && (
              <div className="mt-m">
                <Text.BodyText5 bold className="d-block mb-xs">
                  {t('maximum_discount')}
                </Text.BodyText5>
                <MoneyInput
                  value={maximumDiscount}
                  onChange={onMaximumDiscountChange}
                  testId="maximum-discount-input"
                  ariaLabel={t('maximum_discount')}
                  placeholder={t('modal.maximum_discount_placeholder')}
                />
              </div>
            )}
          </div>

          <hr className="mb-l" />

          <div className="mb-l">
            <div className="d-flex align-items-start justify-content-between">
              <div className="mr-m">
                <Text.BodyText4 bold className="d-block mb-xxs">
                  {t('modal.minimum_spend_title')}
                </Text.BodyText4>
                <Text.BodyText5 className="grey-70">
                  {t('modal.minimum_spend_description')}
                </Text.BodyText5>
              </div>
              <input
                type="checkbox"
                role="switch"
                checked={minSpendOpen}
                onChange={e => {
                  setMinSpendOpen(e.target.checked)
                  // Clear the amount when disabling so the save payload drops
                  // the restriction. Do NOT seed a value when enabling — an
                  // empty field is immediately typeable, whereas a seeded "0"
                  // blocks entry and has to be deleted first.
                  if (!e.target.checked) onMinimumSpendChange('')
                }}
                className="toggle-switch"
                data-testid="minimum-spend-toggle"
                aria-label={t('modal.minimum_spend_title')}
              />
            </div>
            {minSpendOpen && (
              <div className="mt-m">
                <Text.BodyText5 bold className="d-block mb-xs">
                  {t('minimum_spend')}
                </Text.BodyText5>
                <MoneyInput
                  value={minimumSpend}
                  onChange={onMinimumSpendChange}
                  testId="minimum-spend-input"
                  ariaLabel={t('minimum_spend')}
                  placeholder={t('modal.minimum_spend_placeholder')}
                  ariaInvalid={Boolean(minimumSpendError)}
                  ariaDescribedBy={
                    minimumSpendError ? minimumSpendErrorId : undefined
                  }
                />
              </div>
            )}
          </div>

          <hr className="mb-l" />

          {/* Days-of-week restriction */}
          <div className="mb-l">
            <div className="d-flex align-items-start justify-content-between">
              <div className="mr-m">
                <Text.BodyText4 bold className="d-block mb-xxs">
                  {t('modal.days_title')}
                </Text.BodyText4>
                <Text.BodyText5 className="grey-70">
                  {t('modal.days_description')}
                </Text.BodyText5>
              </div>
              <input
                type="checkbox"
                role="switch"
                checked={daysEnabled}
                disabled={advancedRestrictionsLocked}
                onChange={e => onDaysEnabledChange(e.target.checked)}
                className="toggle-switch"
                data-testid="days-toggle"
                aria-label={t('modal.days_title')}
                // Point screen readers at the explanatory banner when the
                // toggle is locked, so users hear "this promo has advanced
                // day/time restrictions configured outside the wizard"
                // instead of just "disabled" with no context.
                aria-describedby={
                  advancedRestrictionsLocked
                    ? 'advanced-locked-banner'
                    : undefined
                }
              />
            </div>
            {daysEnabled && (
              <DayTimePicker
                showDays
                showTime={false}
                days={days}
                onDaysChange={onDaysChange}
                startHour={startHour}
                endHour={endHour}
                onStartHourChange={onStartHourChange}
                onEndHourChange={onEndHourChange}
                description={timeDescription}
                onDescriptionChange={onTimeDescriptionChange}
              />
            )}
          </div>

          <hr className="mb-l" />

          {/* Time-of-day restriction */}
          <div className="mb-m">
            <div className="d-flex align-items-start justify-content-between">
              <div className="mr-m">
                <Text.BodyText4 bold className="d-block mb-xxs">
                  {t('modal.time_title')}
                </Text.BodyText4>
                <Text.BodyText5 className="grey-70">
                  {t('modal.time_description')}
                </Text.BodyText5>
              </div>
              <input
                type="checkbox"
                role="switch"
                checked={timeEnabled}
                disabled={advancedRestrictionsLocked}
                onChange={e => onTimeEnabledChange(e.target.checked)}
                className="toggle-switch"
                data-testid="time-toggle"
                aria-label={t('modal.time_title')}
                // See companion comment on the days toggle above.
                aria-describedby={
                  advancedRestrictionsLocked
                    ? 'advanced-locked-banner'
                    : undefined
                }
              />
            </div>
            {timeEnabled && (
              <DayTimePicker
                showDays={false}
                showTime
                days={days}
                onDaysChange={onDaysChange}
                startHour={startHour}
                endHour={endHour}
                onStartHourChange={onStartHourChange}
                onEndHourChange={onEndHourChange}
                description={timeDescription}
                onDescriptionChange={onTimeDescriptionChange}
                descriptionError={descriptionError}
                timeError={timeError}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => setShowRestrictions(false)}
            data-testid="restrictions-okay-btn"
            disabled={Boolean(descriptionError) || Boolean(timeError)}
          >
            {t('modal.okay')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default RedemptionSection
